import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { MarketplaceInfo } from 'src/api/v1-api';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { isNewAdminDashboardTier } from 'src/constants/tier-features';

export interface BasicInfoFormFields {
  name: string;
  description: string;
  announcement: string;
  mailto: string;
}

export interface BasicInfoFormProps {
  marketplace: MarketplaceInfo | null;
  isLoading?: boolean;
  onSubmit: (data: BasicInfoFormFields) => void;
}

export const BasicInfoForm = ({ marketplace, isLoading, onSubmit }: BasicInfoFormProps) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [announcement, setAnnouncement] = useState('');
  const [mailto, setMailto] = useState('');

  const router = useRouter();
  const isAffiliate = isNewAdminDashboardTier(marketplace?.tier?.name);

  useEffect(() => {
    if (marketplace) {
      setName(marketplace.name || '');
      setDescription(marketplace.description || '');
      setAnnouncement(marketplace.functional?.announcement || '');
      setMailto(marketplace.functional?.mailto || '');
    }
  }, [marketplace]);

  if (!marketplace) {
    return <div>Loading...</div>;
  }

  const tierName = marketplace?.tier?.name || '';
  const pendingCancelMessage =
    marketplace?.pending_cancelled_tier_position &&
    (marketplace?.pending_cancelled_tier_position || 0) === (marketplace?.tier?.position || 0)
      ? ` (pending downgrade)`
      : '';

  const onTierOptionsClick = () => {
    router.push(`/admin/tier-options`);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name,
      description,
      announcement: announcement.trim(),
      mailto,
    });
  };

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%' }}>
      <Stack justify="flex-start" align="flex-start" spacing="24px">
        <FormControl>
          <FormLabel fontWeight="medium" mb="8px">
            Name
          </FormLabel>
          <Input
            placeholder="Marketplace Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </FormControl>

        <FormControl>
          <FormLabel fontWeight="medium" mb="8px">
            Description
          </FormLabel>
          <Textarea
            placeholder="Describe your marketplace"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={4}
            data-test="description-input"
          />
        </FormControl>

        <FormControl>
          <FormLabel fontWeight="medium" mb="8px">
            Announcement
          </FormLabel>
          <Text fontSize="sm" color="gray.600" mb="8px">
            Display an important message to visitors at the top of your pages
          </Text>
          <Textarea
            placeholder="Announcement text (optional)"
            value={announcement}
            onChange={(event) => setAnnouncement(event.target.value)}
            rows={3}
          />
        </FormControl>

        {!isAffiliate && (
          <FormControl>
            <FormLabel fontWeight="medium" mb="8px">
              Support Email
            </FormLabel>
            <Text fontSize="sm" color="gray.600" mb="8px">
              Email address for customer support and contact inquiries
            </Text>
            <Input
              type="email"
              placeholder="support@yourdomain.com"
              value={mailto}
              onChange={(e) => setMailto(e.target.value)}
            />
          </FormControl>
        )}

        <FormControl>
          <FormLabel fontWeight="medium" mb="8px">
            Current Tier
          </FormLabel>
          <Text fontSize="sm" color="gray.600" mb="8px">
            Your current tier is <b>{tierName}</b>
            {pendingCancelMessage}.
          </Text>
          <Button colorScheme="primary" onClick={onTierOptionsClick}>
            View Tier Options
          </Button>
        </FormControl>
      </Stack>
      <Flex direction={'row'} justifyContent={'flex-end'} alignItems={'center'} pt={10}>
        <Box display="flex" justifyContent="flex-end">
          <Link href="/">
            <Button variant="outline" colorScheme="primary" mr={4}>
              Cancel
            </Button>
          </Link>
          <Button isLoading={isLoading} colorScheme="primary" type="submit" data-test="submit-btn">
            Save
          </Button>
        </Box>
      </Flex>
    </form>
  );
};

export default BasicInfoForm;
